import { createContext, useContext, ReactNode } from 'react';
import { useHomeAttachments, Attachment } from '../hooks/useHomeAttachments';

// Define the shape of the context
interface HomeAttachmentsContextProps {
  attachments: Attachment[];
  isLoading: boolean;
  isUploading: boolean;
  isProcessing: boolean;
  isUploadingDocuments: boolean;
  error: string | null;
  uploadProgress: number;
  documentUploadProgress: number;
  uploadFiles: (files: FileList | File[] | null) => Promise<boolean>;
  uploadDocuments: (files: FileList | File[] | null) => Promise<boolean>;
  deleteAttachment: (attachmentId: string) => Promise<boolean>;
  fetchAttachments: () => Promise<void>;
  getAttachmentUrl: (attachment: Attachment) => string;
  isImageFile: (fileType: string) => boolean;
  isDocumentFile: (fileType: string) => boolean;
  ALLOWED_FILE_TYPES: string[];
  ALLOWED_IMAGE_TYPES: string[];
  ALLOWED_DOCUMENT_TYPES: string[];
  homeId?: string;
  getAttachmentById: (attachmentId: string) => Attachment | undefined;
}

// Create the context
export const HomeAttachmentsContext = createContext<HomeAttachmentsContextProps | undefined>(undefined);

interface HomeAttachmentsProviderProps {
  children: ReactNode;
  homeId: string;
  onSuccess?: (message: string) => void;
  onError?: (error: string) => void;
}

export function HomeAttachmentsProvider({ 
  children, 
  homeId,
  onSuccess,
  onError
}: HomeAttachmentsProviderProps) {
  // Use the existing hook with the homeId override
  const attachmentsData = useHomeAttachments({
    homeIdOverride: homeId,
    onSuccess,
    onError
  });
  
  return (
    <HomeAttachmentsContext.Provider value={attachmentsData}>
      {children}
    </HomeAttachmentsContext.Provider>
  );
}

// Custom hook to use the context
export function useHomeAttachmentsContext() {
  const context = useContext(HomeAttachmentsContext);
  if (context === undefined) {
    throw new Error('useHomeAttachmentsContext must be used within a HomeAttachmentsProvider');
  }
  return context;
}